/**
 * PaymentsTab — полная сборка вкладки «Оплаты».
 *
 * Композиция (сверху вниз по макету):
 *   Шапка (иконка+«Оплаты» · «Добавить платёж») → StatsHeader → BudgetBanner (+Finalize) →
 *   тулбар (FiltersBar · Шаблон графика · Экспорт) → PaymentsTable (+ инлайн-добавление) →
 *   Timeline «История оплат». Диалоги (оплата/правка/перенос/подтверждения) и HistoryDrawer — оверлеями.
 *
 * Данные: usePayments/useStats/useEvents (react-query). Live-обновление — useSSE:
 *   payment.updated → патч строки; payment.deleted → удаление; stats → merge агрегатов; event.created → лента.
 * Мутации инкапсулированы в api.ts (оптимистичные апдейты, If-Match, идемпотентность).
 * Права — usePermissions(); контролы правки скрыты для VIEW, замок оплаченного — для не-EDIT_PAID.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  EventLogDto,
  PageEnvelope,
  PaymentDto,
  SseEvent,
  SsePaymentUpdated,
  SseStats,
  StatsDto,
} from '../../lib/types';
import { queryKeys } from '../../lib/queryClient';
import { usePermissions } from '../../lib/permissions';
import { ApiError } from '../../lib/apiClient';
import { useSSE } from '../../hooks/useSSE';

import {
  useAddTransaction,
  useCreatePayment,
  useDeletePayment,
  useEvents,
  usePayments,
  useStats,
  useUpdatePayment,
  type DealScope,
} from './api';
import { StatsHeader } from './StatsHeader';
import { BudgetBanner } from './BudgetBanner';
import { FiltersBar, EMPTY_FILTERS, type PaymentFilters } from './FiltersBar';
import { TemplatePicker } from './TemplatePicker';
import { PaymentsTable } from './PaymentsTable';
import type { AddPaymentDraft } from './AddPaymentRow';
import { Timeline } from './Timeline';
import { HistoryDrawer } from './HistoryDrawer';
import { PartialPaymentDialog } from './dialogs/PartialPaymentDialog';
import { RescheduleDialog } from './dialogs/RescheduleDialog';
import { EditPaymentDialog } from './dialogs/EditPaymentDialog';
import { ConfirmDialog } from './dialogs/ConfirmDialog';
import { Button } from './ui/Button';
import { IconPlus, IconWallet } from './ui/icons';
import { useToasts, ToastHost } from './ui/toasts';

export interface PaymentsTabProps {
  /** amoCRM deal id (навигационный, строкой). */
  dealId: string;
  /** amoCRM account id (для скоупа ключей кэша). */
  accountId: string;
}

type ActiveDialog =
  | { kind: 'pay'; payment: PaymentDto }
  | { kind: 'edit'; payment: PaymentDto }
  | { kind: 'reschedule'; payment: PaymentDto }
  | { kind: 'cancel'; payment: PaymentDto }
  | { kind: 'delete'; payment: PaymentDto }
  | null;

/** Ключ идемпотентности для проведения факта при создании транша (повтор по refresh не задвоит оплату). */
function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pw-add-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function PaymentsTab({ dealId, accountId }: PaymentsTabProps): JSX.Element {
  const scope: DealScope = { accountId, dealId };
  const qc = useQueryClient();
  const { canEdit, canEditPaid, canManageSettings } = usePermissions();
  const toasts = useToasts();

  const [filters, setFilters] = useState<PaymentFilters>(EMPTY_FILTERS);
  const [addMode, setAddMode] = useState(false);
  const [dialog, setDialog] = useState<ActiveDialog>(null);
  const [historyFor, setHistoryFor] = useState<PaymentDto | null>(null);

  const paymentsQuery = usePayments(scope);
  const statsQuery = useStats(scope);
  const eventsQuery = useEvents(scope);

  const updateMut = useUpdatePayment(scope);
  const deleteMut = useDeletePayment(scope);
  const createMut = useCreatePayment(scope);
  const addTxMut = useAddTransaction(scope);

  // --- SSE: live-обновление кэша (имена событий из фундамента useSSE/types) ---
  const onSse = useCallback(
    (event: SseEvent) => {
      switch (event.name) {
        case 'payment.updated':
          patchPaymentInCache(qc, scope, event.data);
          void qc.invalidateQueries({ queryKey: queryKeys.stats(accountId, dealId) });
          // Перенос срока приходит без новых сумм/дат в патчабельном виде — перечитываем список.
          if (event.data.newDate !== undefined) {
            void qc.invalidateQueries({ queryKey: queryKeys.payments(accountId, dealId) });
          }
          break;
        case 'payment.deleted':
          void qc.invalidateQueries({ queryKey: queryKeys.payments(accountId, dealId) });
          void qc.invalidateQueries({ queryKey: queryKeys.stats(accountId, dealId) });
          break;
        case 'stats':
          mergeStatsInCache(qc, scope, event.data);
          break;
        case 'event.created':
          // Лёгкая инвалидация ленты (порядок/агрегация — на сервере).
          void qc.invalidateQueries({ queryKey: queryKeys.events(accountId, dealId) });
          break;
        case 'task.created':
          break;
        case 'error':
          // Поток сообщил об ошибке — данные подтянет следующая инвалидация/рефетч.
          break;
      }
    },
    [qc, scope, accountId, dealId],
  );

  useSSE(dealId, { onEvent: onSse });

  // --- Инлайн-автосохранение ячеек ---
  const handleCommitField = useCallback(
    (payment: PaymentDto, patch: { plannedDate?: string; plannedAmountMinor?: string }) => {
      updateMut.mutate(
        { id: payment.id, version: payment.version, patch },
        {
          onError: (e) => {
            if (e instanceof ApiError && e.isVersionConflict) {
              toasts.push('Транш изменён в другом месте — обновляю данные.', 'info');
            } else if (e instanceof ApiError && e.isForbidden) {
              toasts.push('Недостаточно прав для изменения этого транша.', 'error');
            } else {
              toasts.push('Не удалось сохранить изменение.', 'error');
            }
          },
        },
      );
    },
    [updateMut, toasts],
  );

  // --- Добавление нового транша (с необязательным фактом оплаты в той же строке) ---
  const handleAddConfirm = useCallback(
    (draft: AddPaymentDraft) => {
      const { actualDate, paidAmountMinor, ...planBody } = draft;
      createMut.mutate(
        { body: planBody },
        {
          onSuccess: (created) => {
            setAddMode(false);
            // Если менеджер сразу указал факт — проводим оплату по только что созданному траншу.
            if (paidAmountMinor && actualDate && BigInt(paidAmountMinor) > 0n) {
              addTxMut.mutate(
                {
                  paymentId: created.id,
                  idempotencyKey: makeIdempotencyKey(),
                  body: {
                    amountMinor: paidAmountMinor,
                    actualDate,
                    paidAt: new Date(`${actualDate}T12:00:00`).toISOString(),
                  },
                },
                {
                  onError: (e) => {
                    toasts.push(
                      e instanceof ApiError
                        ? e.message || 'Транш создан, но факт оплаты не проведён.'
                        : 'Транш создан, но факт оплаты не проведён.',
                      'error',
                    );
                  },
                },
              );
            }
          },
          onError: (e) => {
            toasts.push(e instanceof ApiError ? e.message || 'Не удалось добавить транш.' : 'Не удалось добавить транш.', 'error');
          },
        },
      );
    },
    [createMut, addTxMut, toasts],
  );

  // --- Отмена/удаление через подтверждение ---
  const confirmCancelOrDelete = useCallback(() => {
    if (!dialog || (dialog.kind !== 'cancel' && dialog.kind !== 'delete')) return;
    const { payment } = dialog;
    deleteMut.mutate(
      { id: payment.id, version: payment.version },
      {
        onSuccess: () => {
          setDialog(null);
          toasts.push(dialog.kind === 'delete' ? 'Транш удалён.' : 'Платёж отменён.', 'success');
        },
        onError: (e) => {
          if (e instanceof ApiError && e.isVersionConflict) {
            toasts.push('Транш изменён в другом месте — обновляю данные.', 'info');
            setDialog(null);
          } else {
            toasts.push('Не удалось выполнить действие.', 'error');
          }
        },
      },
    );
  }, [dialog, deleteMut, toasts]);

  const payments = paymentsQuery.data?.data ?? [];
  const stats = statsQuery.data ?? null;
  const events: EventLogDto[] = eventsQuery.data?.data ?? [];

  const isLoading = paymentsQuery.isLoading || statsQuery.isLoading;
  const isError = paymentsQuery.isError || statsQuery.isError;
  const isEmpty = !isLoading && !isError && payments.length === 0 && !addMode;

  // --- Экраны состояний ---
  if (isError) {
    return (
      <div className="pw-tab">
        <PaymentsHeader canEdit={false} onAdd={() => undefined} addDisabled />
        <div className="pw-state" role="alert">
          <div className="pw-state__title">Не удалось загрузить платежи</div>
          <p className="pw-state__hint">Проверьте соединение и повторите попытку.</p>
          <Button
            variant="primary"
            onClick={() => {
              void paymentsQuery.refetch();
              void statsQuery.refetch();
            }}
          >
            Повторить
          </Button>
        </div>
        <ToastHost toasts={toasts.toasts} dismiss={toasts.dismiss} />
      </div>
    );
  }

  return (
    <div className="pw-tab">
      <PaymentsHeader
        canEdit={canEdit}
        onAdd={() => setAddMode(true)}
        addDisabled={addMode || isLoading}
      />

      {isLoading ? (
        <div className="pw-state" aria-busy="true">
          <span className="pw-spinner" aria-hidden />
          <div className="pw-state__title">Загрузка платежей…</div>
        </div>
      ) : (
        <>
          {stats ? <StatsHeader stats={stats} /> : null}
          {stats ? <BudgetBanner scope={scope} stats={stats} canManageSettings={canManageSettings} /> : null}

          <div className="pw-toolbar">
            <FiltersBar value={filters} onChange={setFilters} />
            <div className="pw-toolbar__right">
              {canManageSettings ? <TemplatePicker scope={scope} /> : null}
            </div>
          </div>

          {updateMut.isPending ? (
            <div className="pw-autosave" aria-live="polite">
              <span className="pw-autosave__dot" aria-hidden />
              Изменения сохраняются автоматически…
            </div>
          ) : null}

          {isEmpty ? (
            <div className="pw-state pw-state--empty">
              <div className="pw-state__title">Платежей пока нет</div>
              <p className="pw-state__hint">
                {canEdit
                  ? 'Добавьте первый транш или примените шаблон графика.'
                  : 'План оплат ещё не настроен.'}
              </p>
              {canEdit ? (
                <Button variant="primary" icon={<IconPlus size={16} />} onClick={() => setAddMode(true)}>
                  Добавить платёж
                </Button>
              ) : null}
            </div>
          ) : (
            <PaymentsTable
              data={payments}
              filters={filters}
              addMode={addMode}
              addPending={createMut.isPending || addTxMut.isPending}
              onAddConfirm={handleAddConfirm}
              onAddCancel={() => setAddMode(false)}
              meta={{
                canEdit,
                canEditPaid,
                onCommitField: handleCommitField,
                onRegisterPayment: (p) => setDialog({ kind: 'pay', payment: p }),
                onEdit: (p) => setDialog({ kind: 'edit', payment: p }),
                onReschedule: (p) => setDialog({ kind: 'reschedule', payment: p }),
                onHistory: (p) => setHistoryFor(p),
                onCancel: (p) => setDialog({ kind: 'cancel', payment: p }),
                onDelete: (p) => setDialog({ kind: 'delete', payment: p }),
              }}
            />
          )}

          <Timeline events={events} isLoading={eventsQuery.isLoading} />
        </>
      )}

      {/* --- Диалоги --- */}
      {dialog?.kind === 'pay' ? (
        <PartialPaymentDialog scope={scope} payment={dialog.payment} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'edit' ? (
        <EditPaymentDialog scope={scope} payment={dialog.payment} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'reschedule' ? (
        <RescheduleDialog scope={scope} payment={dialog.payment} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'cancel' ? (
        <ConfirmDialog
          title={`Отменить платёж №${dialog.payment.number}?`}
          message="Транш будет помечен как «Отменён». Уже проведённые оплаты сохранятся в истории."
          confirmLabel="Отменить платёж"
          pending={deleteMut.isPending}
          onConfirm={confirmCancelOrDelete}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          title={`Удалить платёж №${dialog.payment.number}?`}
          message="Транш будет удалён из графика. Действие необратимо."
          confirmLabel="Удалить"
          pending={deleteMut.isPending}
          onConfirm={confirmCancelOrDelete}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {historyFor ? (
        <HistoryDrawer
          scope={scope}
          paymentId={historyFor.id}
          paymentNumber={historyFor.number}
          onClose={() => setHistoryFor(null)}
        />
      ) : null}

      <ToastHost toasts={toasts.toasts} dismiss={toasts.dismiss} />
    </div>
  );
}

/** Шапка вкладки: иконка+«Оплаты» слева, синяя «Добавить платёж» справа. */
function PaymentsHeader({
  canEdit,
  onAdd,
  addDisabled,
}: {
  canEdit: boolean;
  onAdd: () => void;
  addDisabled?: boolean;
}): JSX.Element {
  return (
    <header className="pw-header">
      <div className="pw-header__title">
        <span className="pw-header__icon" aria-hidden>
          <IconWallet size={20} />
        </span>
        <h1 className="pw-header__heading">Оплаты</h1>
      </div>
      {canEdit ? (
        <Button variant="primary" icon={<IconPlus size={16} />} onClick={onAdd} disabled={addDisabled}>
          Добавить платёж
        </Button>
      ) : null}
    </header>
  );
}

// ---------------------------------------------------------------------------
// SSE-хелперы кэша
// ---------------------------------------------------------------------------

/** Точечный патч строки списка по данным payment.updated. */
function patchPaymentInCache(
  qc: ReturnType<typeof useQueryClient>,
  scope: DealScope,
  data: SsePaymentUpdated,
): void {
  const key = queryKeys.payments(scope.accountId, scope.dealId);
  qc.setQueryData<PageEnvelope<PaymentDto>>(key, (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      data: prev.data.map((p) =>
        p.id === data.paymentId
          ? {
              ...p,
              // ЧАСТИЧНЫЙ мерж: события автоматизации шлют неполные payload'ы
              // ({paymentId,status} или {paymentId,newDate}) — слепая перезапись
              // делала суммы undefined и клала всю вкладку (белый экран, app_crash).
              ...(data.number !== undefined ? { number: data.number } : {}),
              ...(data.status !== undefined ? { status: data.status } : {}),
              ...(data.plannedAmountMinor !== undefined
                ? { plannedAmountMinor: data.plannedAmountMinor }
                : {}),
              ...(data.paidAmountMinor !== undefined ? { paidAmountMinor: data.paidAmountMinor } : {}),
              ...(data.remainderMinor !== undefined ? { remainderMinor: data.remainderMinor } : {}),
              ...(data.version !== undefined ? { version: data.version } : {}),
            }
          : p,
      ),
    };
  });
}

/** Merge частичного среза stats из SSE в закэшированный StatsDto (без потери прочих полей). */
function mergeStatsInCache(qc: ReturnType<typeof useQueryClient>, scope: DealScope, data: SseStats): void {
  const key = queryKeys.stats(scope.accountId, scope.dealId);
  qc.setQueryData<StatsDto>(key, (prev) => {
    if (!prev) {
      // Нет базового снимка — перечитаем полноценный stats.
      void qc.invalidateQueries({ queryKey: key });
      return prev;
    }
    return {
      ...prev,
      paidMinor: data.paidMinor,
      remainderMinor: data.remainderMinor,
      paidPercent: data.paidPercent,
      overdueMinor: data.overdueMinor,
      overdueCount: data.overdueCount,
      budgetMatches: data.budgetMatches,
      plannedTotalMinor: data.plannedTotalMinor,
    };
  });
}
